import { useRef } from 'react';
import { useFocusTrap } from '../hooks/useFocusTrap';
import type { DownloadFormat } from '../types/roll';
import styles from './FormatDialog.module.css';

const FORMATS: { format: DownloadFormat; label: string; description: string }[] = [
  { format: 'social_jpeg', label: 'Social JPEG', description: '≤2048px, ≤2 MB' },
  { format: 'print_jpeg', label: 'Print JPEG', description: 'Full resolution' },
  { format: 'archival_tiff', label: 'Archival TIFF', description: 'Original master file' },
];

export interface FormatDialogProps {
  /** What's being downloaded, e.g. "All 36 scans" or "3 selected scans". */
  summary: string;
  /**
   * Edits have no TIFF master, so the archival option delivers them as their
   * print JPEG; say so when the download includes any.
   */
  hasEdits: boolean;
  onSelect: (format: DownloadFormat) => void;
  onClose: () => void;
}

/** Asks which format to download in. Render it only while it's open. */
export function FormatDialog({ summary, hasEdits, onSelect, onClose }: FormatDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, onClose);

  return (
    // Only a click on the backdrop itself closes; clicks inside the dialog
    // land on its children. Escape is handled by the focus trap.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={styles.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="format-dialog-heading"
        aria-describedby="format-dialog-summary"
        tabIndex={-1}
      >
        <h2 id="format-dialog-heading" className={styles.heading}>
          Choose download format
        </h2>
        <p id="format-dialog-summary" className={styles.summary}>
          {summary}
        </p>
        <div className={styles.options}>
          {FORMATS.map(({ format, label, description }) => (
            <button
              key={format}
              type="button"
              className={styles.option}
              onClick={() => onSelect(format)}
              aria-label={`Download as ${label}`}
            >
              <strong>{label}</strong>
              <span className={styles.optionDetail}>
                {description}
                {format === 'archival_tiff' && hasEdits && ' — edited images are delivered as JPEG'}
              </span>
            </button>
          ))}
        </div>
        <button type="button" className={styles.cancel} onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );
}
